import { useCallback, useEffect, useMemo, useState, type FC } from "react";

import {
  caseFileStore,
  type FileCaseContext
} from "../../caseFiles/editableFile";
import { nodeTitle } from "../../caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import { useCaseIde } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { artifactKey } from "../types";
import { CopySourceStrip } from "./CopySourceStrip";
import { FileViewer, Notice } from "./FileViewer";

type Props = {
  node: FsNode | null;
  /** Formatted exhibit label ("Exhibit A") or empty. */
  label: string;
  page?: number | null;
};

/**
 * A case file open in the center pane: `FileViewer` over the node, plus what
 * only the Case IDE has — the copy-source strip, the chat's selection, the
 * live agent and Make This a Draft. Every save is a new version of the node
 * and refreshes the tree.
 */
export const FilePane: FC<Props> = ({ node, label, page }) => {
  const { individualId, setSelectedText, open, close, invalidate } =
    useCaseIde();
  const { fs } = useCaseData();
  // Bumped when the copy strip updates the file from its source.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => () => setSelectedText(null), [setSelectedText]);

  const store = useMemo(
    () => (node ? caseFileStore(individualId, node) : null),
    [individualId, node]
  );
  const caseContext = useMemo<FileCaseContext>(
    () => ({
      individualId,
      onDraftCreated: (draftId, title) => {
        invalidate(["drafts"]);
        open({ kind: "draft", id: draftId, title });
      }
    }),
    [individualId, invalidate, open]
  );

  const handleSaved = useCallback(() => {
    void fs.refresh();
    invalidate(["fs"]);
  }, [fs, invalidate]);
  const handleRestored = useCallback(() => {
    void fs.refresh();
  }, [fs]);
  const nodeId = node?.id ?? null;
  const handleClose = useCallback(() => {
    if (nodeId) close(artifactKey({ kind: "file", id: nodeId }));
  }, [close, nodeId]);

  if (!node || !store) {
    return <Notice>This file is no longer in the case.</Notice>;
  }
  if (node.kind === "folder") {
    return (
      <Notice>
        {nodeTitle(node)} is a folder. Open a file inside it to read it.
      </Notice>
    );
  }

  return (
    <FileViewer
      store={store}
      label={label}
      page={page}
      caseContext={caseContext}
      topSlot={
        node.source_ref ? (
          <CopySourceStrip
            node={node}
            onUpdated={() => setReloadKey((k) => k + 1)}
          />
        ) : null
      }
      reloadKey={reloadKey}
      onSaved={handleSaved}
      onRestored={handleRestored}
      onClose={handleClose}
      onTextSelectionChange={setSelectedText}
    />
  );
};
